import { useCallback, useRef, useEffect } from 'react';
import { useApi } from './useApi';
import { useAuth } from './useAuth';

export type AnalyticsEventType = 'page_view' | 'action' | 'session_start';

interface UseAnalyticsReturn {
    trackPageView: (page: string) => void;
    trackAction: (action: string, metadata?: Record<string, any>) => void;
    trackSessionStart: () => void;
}

/**
 * Analytics tracking hook.
 * Tracks user activity (page views, actions) for admin insights.
 */
export function useAnalytics(): UseAnalyticsReturn {
    const api = useApi();
    const { isAuthenticated, user } = useAuth();
    const lastPageRef = useRef<string | null>(null);
    const sessionTrackedRef = useRef(false);

    // Track session start once per mount
    useEffect(() => {
        if (isAuthenticated && !sessionTrackedRef.current) {
            sessionTrackedRef.current = true;
            api.logAnalyticsEvent('session_start', {}).catch(() => {
                // Silently fail - analytics shouldn't break the app
            });
        }
    }, [isAuthenticated, api]);

    const trackPageView = useCallback((page: string) => {
        if (!isAuthenticated) return;

        // Prevent duplicate tracking of same page
        if (lastPageRef.current === page) return;
        lastPageRef.current = page;

        api.logAnalyticsEvent('page_view', { page }).catch(() => {
            // Silently fail
        });
    }, [isAuthenticated, api]);

    const trackAction = useCallback((action: string, metadata?: Record<string, any>) => {
        if (!isAuthenticated) return;

        api.logAnalyticsEvent('action', { action, metadata }).catch(() => {
            // Silently fail
        });
    }, [isAuthenticated, api]);

    const trackSessionStart = useCallback(() => {
        if (!isAuthenticated) return;

        api.logAnalyticsEvent('session_start', {}).catch(() => {
            // Silently fail
        });
    }, [isAuthenticated, api]);

    return {
        trackPageView,
        trackAction,
        trackSessionStart
    };
}
